import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import {
  useElementListColumnResizeHandle,
  ELEMENT_LIST_RESIZE_HANDLE_CLASS,
  ELEMENT_LIST_WIDTH_CSS,
} from '@features/proposals/ElementListRow/element-list-column-resize.hook';
import { FocusViewSegment } from '@client/hooks/use-element-focus.hook';
import {
  useFocusViewBackNavigation,
  useOpenFocusElement,
  useOpenFocusGroup,
  useFocusElement,
} from '@client/hooks/use-element-focus.hook';
import {
  BreadcrumbRow,
  BreadcrumbSegment,
  extractGroupPrefix,
} from '@features/elements/components/ElementBreadcrumb';
import {
  useAnyRowShowingColumns,
  setElementListColumnsHovered,
} from '@features/proposals/ElementListRow/element-list-column-hover.store';
import { ElementListSortControl } from '@features/proposals/ElementListRow/ElementListSortControl';
import { useBuildHeaderStickyReplace } from '@features/proposals/ElementListRow/use-build-header-sticky-replace.hook';
import MultiLineTooltip from '@ui/MultiLineTooltip';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { currencyIcons } from '@ui/Icon/Icon';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useAddElement } from '@client/store/project';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { ElementCategoryMenu } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenu';
import { focusViewSystemElementAddOptions } from '@shared/helpers/element.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { OneOfFactoryElements } from '@shared/models/factory-element.model';
import { ElementKind } from '@shared/models/project.model';
import { FOCUS_VIEW_HEADER_HEIGHT } from '@features/proposals/ElementListRow';

type FocusViewHeaderProps = {
  segments: FocusViewSegment[];
};

export const FocusViewHeader: React.FC<FocusViewHeaderProps> = ({
  segments,
}) => {
  const { classes } = useStyles();
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  useBuildHeaderStickyReplace(stickySentinelRef);
  const handleDragStart = useElementListColumnResizeHandle();
  const handleBack = useFocusViewBackNavigation();
  const openFocusGroup = useOpenFocusGroup();
  const openFocusElement = useOpenFocusElement();
  const anyRowActive = useAnyRowShowingColumns();
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  const readonly = useIsReadonly();
  const focusElement = useFocusElement();
  const addElement = useAddElement();
  const categoryGroups = useGroupedElementCategories();
  const [createElementAnchor, setCreateElementAnchor] =
    useState<HTMLElement | null>(null);

  const handleAddElement = useCallback(
    async (categoryId: ElementCategoryID) => {
      if (!focusElement) return;
      const defaults: OneOfFactoryElements = {
        kind: ElementKind.Element,
        category_id: categoryId,
      };
      // Product-slot rows cannot contain a system element; place it beside them.
      await addElement(
        focusElement,
        defaults,
        focusViewSystemElementAddOptions(focusElement),
      );
      setCreateElementAnchor(null);
    },
    [focusElement, addElement],
  );

  const handleSegmentClick = useCallback(
    (id: string) => {
      const seg = segments.find((s) => s.id === id);
      if (!seg) return;
      if (seg.type === 'group') openFocusGroup(id);
      else openFocusElement(id);
    },
    [segments, openFocusGroup, openFocusElement],
  );

  const breadcrumbSegments = useMemo<BreadcrumbSegment[]>(
    () =>
      segments.map((seg) => {
        if (seg.type === 'group') {
          const extracted = extractGroupPrefix(seg.label);
          return {
            id: seg.id,
            label: extracted?.restLabel ?? seg.label,
            prefix: extracted?.prefix,
            icon: seg.icon,
            clickable: !seg.disabled,
          };
        }
        return {
          id: seg.id,
          label: seg.label,
          icon: seg.icon,
          clickable: !seg.disabled,
        };
      }),
    [segments],
  );

  if (!segments.length) return null;

  return (
    <>
      <Box
        ref={stickySentinelRef}
        aria-hidden
        sx={{ height: 0, width: '100%' }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: FOCUS_VIEW_HEADER_HEIGHT,
          width: '100%',
          position: 'sticky',
          top: 0,
          // Replaces the Build header (z-index 3) when this header becomes sticky.
          zIndex: 4,
          backgroundColor: 'warmSurface.main',
        }}
      >
        {/* Resizable left column */}
        <Box
          className={classes.leftColumn}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            height: '100%',
            width: ELEMENT_LIST_WIDTH_CSS,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pl: '4px',
              height: '100%',
              width: '100%',
            }}
          >
            {/* Back arrow — same position as the search icon */}
            <Tooltip title="Back" placement="bottom" disableInteractive>
              <Box
                onClick={handleBack}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: '6px',
                  flexShrink: 0,
                  opacity: 0.6,
                  cursor: 'pointer',
                  '&:hover': { opacity: 1 },
                }}
              >
                <Icon category="mui" icon="ArrowBack" size={12} />
              </Box>
            </Tooltip>

            {/* Breadcrumb — where tabs normally live */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                mr: '4px',
              }}
            >
              <BreadcrumbRow
                segments={breadcrumbSegments}
                onSegmentClick={handleSegmentClick}
                nowrap
              />
            </Box>

            {focusElement && !readonly && (
              <Tooltip
                title="New element"
                placement="bottom"
                disableInteractive
              >
                <Box
                  component="button"
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                    setCreateElementAnchor(e.currentTarget)
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: '8px',
                    width: 20,
                    height: 20,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    border: 'none',
                    borderRadius: '5px',
                    backgroundColor: 'transparent',
                    color: 'rgba(0,0,0,0.55)',
                    cursor: 'pointer',
                    p: 0,
                    flexShrink: 0,

                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      color: 'rgba(0,0,0,0.85)',
                    },
                  }}
                >
                  <Icon category="mui" icon="Add" size={13} />
                </Box>
              </Tooltip>
            )}
          </Box>

          <ElementCategoryMenu
            anchor={createElementAnchor}
            categoryGroups={categoryGroups}
            onSelect={handleAddElement}
            onClose={() => setCreateElementAnchor(null)}
          />

          {/* Drag handle */}
          <Box
            className={ELEMENT_LIST_RESIZE_HANDLE_CLASS}
            onMouseDown={handleDragStart}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 4,
              cursor: 'ew-resize',
            }}
          />
        </Box>

        {/* Right column */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            pl: '8px',
            gap: '4px',
            backgroundColor: 'warmSurface.main',
          }}
        >
          <ElementListSortControl />
          {/* CO2 + Cost header cells */}
          <Box
            onMouseEnter={() => setElementListColumnsHovered(true)}
            onMouseLeave={() => setElementListColumnsHovered(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              ml: 'auto',
            }}
          >
            {co2eVisible.isEnabled && (
              <Box
                sx={{
                  width: 60,
                  flexShrink: 0,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.4,
                  visibility: anyRowActive ? 'visible' : 'hidden',
                }}
              >
                <MultiLineTooltip
                  disableInteractive
                  rows={['Carbon kgCO2e per m²']}
                >
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <Icon category="costEmission" icon="kgCO2e" size={18} />
                  </Box>
                </MultiLineTooltip>
              </Box>
            )}

            {!displayCurrency.isDisabled && (
              <Box
                sx={{
                  width: 60,
                  flexShrink: 0,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.4,
                  visibility: anyRowActive ? 'visible' : 'hidden',
                }}
              >
                <MultiLineTooltip
                  disableInteractive
                  rows={[`Expenses ${displayCurrency.current} per m²`]}
                >
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: 'fit-content',
                      display: 'flex',
                    }}
                  >
                    {currencyIcons[displayCurrency.current]}
                  </Box>
                </MultiLineTooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const useStyles = makeStyles()(({ palette }) => ({
  leftColumn: {
    backgroundColor: palette.warmSurface.main,
    boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.1)',
  },
}));
